// The island as the ENGINE knows it: hex math, which hexes are land, where
// the six region sites are, what counts as wild ground, where camps can
// appear. Pure and deterministic. world/layout.js draws from this; the
// Frontier's phone map draws from this; the reducer validates marches
// against this. Nothing here knows about models or pixels.

export const DIRS = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];
export const key = (q, r) => `${q},${r}`;
export const unkey = (k) => k.split(',').map(Number);
export const add = ([q, r], [dq, dr]) => [q + dq, r + dr];
export const hexDistance = (q, r) => Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
export const hexDistanceBetween = ([q1, r1], [q2, r2]) => hexDistance(q1 - q2, r1 - r2);
export const neighbours = (q, r) => DIRS.map(([dq, dr]) => [q + dq, r + dr]);
/** Every hex within `radius` of (q, r), including it. */
export const within = (q, r, radius) => {
  const out = [];
  for (let dq = -radius; dq <= radius; dq += 1) {
    for (let dr = -radius; dr <= radius; dr += 1) {
      if (hexDistance(dq, dr) <= radius) out.push([q + dq, r + dr]);
    }
  }
  return out;
};

// THE WORLD IS GENERATED, NOT STORED (Matt, after playtest 12: "can we like
// procedurally generate the map as we explore? ... the map could kind of
// just be any size and we don't have to preload so much"). The GROUND is a
// pure function of the game's seed and the hex: smooth noise gives an
// irregular coast, a home island that always holds the town and its first
// ring of regions, and seas with other islands beyond (crossing them is a
// later age's work). Nothing about the ground is saved; every screen and
// every reload computes the same land. What is PLACED on it - region sites
// past the first ring, finds, camps - is placed only in the FRONTIER BAND,
// a couple of rings beyond what has been seen, as the band moves out
// (engine/frontier.js). Nothing breeds on ground nobody will see for years.
//
// `seed` everywhere below is `civ.mapSeed` (0 for an old save).

/** The home island's sure ground: the town, its ring, the first regions. */
export const HOME_RADIUS = 4;
/** How far past the seen the frontier band reaches. */
export const BAND = 2;

// A tiny deterministic hash so the scenery is varied but never shuffles
// between renders or between two tables showing the same game.
export const hash = (q, r, salt = 0) => {
  let h = (q * 374761393 + r * 668265263 + salt * 2246822519) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

// Smooth value noise over the hex plane: lattice values from the hash,
// eased between, two octaves. 0..1, about 0.5 on average.
const smooth = (t) => t * t * (3 - 2 * t);
const lattice = (x, y, salt) => {
  const ix = Math.floor(x); const iy = Math.floor(y);
  const fx = smooth(x - ix); const fy = smooth(y - iy);
  const v = (a, b) => hash(a, b, salt);
  const top = v(ix, iy) + (v(ix + 1, iy) - v(ix, iy)) * fx;
  const bot = v(ix, iy + 1) + (v(ix + 1, iy + 1) - v(ix, iy + 1)) * fx;
  return top + (bot - top) * fy;
};
export const noise = (q, r, salt, scale = 5) => {
  const x = (q + r / 2) / scale; const y = (r * 0.866) / scale;
  return lattice(x, y, salt) * 0.65 + lattice(x * 2.1 + 17, y * 2.1 - 9, salt + 1) * 0.35;
};

/**
 * Is this hex land? Sure inside the home radius; past it, land where the
 * noise beats a bar that rises with the distance, so the home island has a
 * ragged coast eight to twelve out and only islands lie beyond.
 */
export const isLandHex = (q, r, seed = 0) => {
  const d = hexDistance(q, r);
  if (d <= HOME_RADIUS) return true;
  // Tuned on five seeds (2026-10-16): 64-93% land five to eight out, a home
  // island of 370-570 walkable hexes, islands only past twenty.
  const bar = Math.min(0.82, 0.24 + 0.03 * (d - HOME_RADIUS));
  return noise(q, r, 1000 + seed * 7) > bar;
};

// ---------------------------------------------------------------- sites
// The FIRST RING: six region sites, one per direction three out, always -
// the first year's map, and the stories that come with it. Past it, sites
// are found as the frontier reaches them (engine/frontier.js): `civ.farSites`
// holds their hexes, numbered on from 6.
export const cluster = (dir, wing, out = 3) => {
  const c = [dir[0] * out, dir[1] * out];
  return [c, add(c, DIRS[(wing + 1) % 6]), add(c, DIRS[(wing + 5) % 6])];
};
export const NEAR_SITES = 6;
export const SITES = DIRS.map((dir, i) => cluster(dir, i));
const SITE_OF = new Map();
SITES.forEach((hexes, d) => hexes.forEach(([q, r]) => SITE_OF.set(key(q, r), d)));
/** Every site's hexes: the first ring, then the ones the frontier has found. */
export const allSites = (civ) => [...SITES, ...((civ && civ.farSites) || [])];
/** Which site a hex belongs to, or -1. */
export const siteAt = (q, r, civ = null) => {
  if (SITE_OF.has(key(q, r))) return SITE_OF.get(key(q, r));
  const far = (civ && civ.farSites) || [];
  for (let i = 0; i < far.length; i += 1) if (far[i].some(([a, b]) => a === q && b === r)) return NEAR_SITES + i;
  return -1;
};
export const siteCentre = (site, civ = null) => allSites(civ)[site]?.[0] || [0, 0];

// ---------------------------------------------------------------- terrain
export const baseTerrain = (q, r, seed = 0) => {
  const d = hexDistance(q, r);
  if (!isLandHex(q, r, seed)) return hash(q, r, 3) < 0.1 ? 'water-rocks' : 'water';
  if (d < 3) return 'grass';
  // The coast is shore.
  if (d > HOME_RADIUS && DIRS.some(([dq, dr]) => !isLandHex(q + dq, r + dr, seed))) return hash(q, r, 2) < 0.5 ? 'sand' : 'sand-rocks';
  const m = noise(q, r, 2000 + seed * 7, 3.5);
  if (m > 0.74) return 'stone-mountain';
  if (m > 0.65) return hash(q, r, 5) < 0.5 ? 'stone-hill' : 'grass-hill';
  if (noise(q, r, 3000 + seed * 7, 3) > 0.56) return 'grass-forest';
  if (hash(q, r, 6) < 0.05) return 'stone-rocks';
  return 'grass';
};
export const isWater = (t) => !t || t.startsWith('water');
/** Land the band can stand on (a site's hexes count once its land is up). */
export const isLand = (q, r, civ = {}) => {
  const site = siteAt(q, r, civ);
  if (site >= 0) {
    const sites = civ.regionSites || {};
    const regionId = sites[site];
    return Boolean(regionId) && (civ.regions || []).includes(regionId);
  }
  return !isWater(baseTerrain(q, r, civ.mapSeed || 0));
};
/** Can the band step here: land, or an unfound site (that is the exploring). */
export const canStand = (q, r, civ = {}) => {
  const site = siteAt(q, r, civ);
  if (site >= 0) return Boolean((civ.regionSites || {})[site]);
  return isLand(q, r, civ);
};
/** Wild ground: where the band hunts (forest, hills, mountain, the shore, any found region). */
export const isWild = (q, r, civ = {}) => {
  if (siteAt(q, r, civ) >= 0) return isLand(q, r, civ);
  const t = baseTerrain(q, r, civ.mapSeed || 0);
  return hexDistance(q, r) >= 3 && !isWater(t);
};

// What each kind of ground is CALLED. Matt, 2026-09-19, reading "the band
// marches to 3,-4": "I don't know what that means." Coordinates are for the
// engine; a person needs a place.
export const TERRAIN_NAMES = {
  grass: 'the meadows',
  'grass-forest': 'the forest',
  'grass-hill': 'the hills',
  'stone-hill': 'the hills',
  'stone-mountain': 'the mountains',
  stone: 'the rocks',
  'stone-rocks': 'the rocks',
  sand: 'the shore',
  'sand-rocks': 'the shore',
  dirt: 'the bare ground',
  water: 'the sea',
  'water-rocks': 'the sea',
  'water-island': 'the sea',
};

/** The town: ring 2 and in. */
export const TOWN_RADIUS = 2;
export const isTown = (q, r) => hexDistance(q, r) <= TOWN_RADIUS;
